<template>
  <div class="page-shell">
    <div class="px-2">
      <!-- <div class="hero-copy mb-2">
        <h2 class="hero-title">My Subscriptions</h2>
        <p class="hero-subtitle">Manage your subscriptions and billing details.</p>
      </div> -->
      <div class="hero-panel">
        <div class="hero-metrics subscription-filters">
          <div
            v-for="item in heroMetricItems"
            :key="item.key"
            class="metric-card"
            role="button"
            tabindex="0"
            :class="[{ 'metric-card--selected': activeTab === item.key }]"
            @click="activeTab = item.key"
            @keydown.enter.prevent="activeTab = item.key"
            @keydown.space.prevent="activeTab = item.key"
          >
            <div class="metric-content">
              <div class="metric-icon" :class="`metric-icon--${item.key.toLowerCase()}`">
                <v-icon size="18" :color="item.iconColor">{{ item.icon }}</v-icon>
              </div>
              <span class="metric-label">{{ item.label }}</span>
            </div>
            <strong class="metric-value">{{ item.value }}</strong>
          </div>
        </div>
      </div>
    </div>

    <v-row v-if="isLoading" dense>
      <v-col v-for="index in 3" :key="index" cols="12">
        <v-skeleton-loader class="subscription-skeleton" type="article" />
      </v-col>
    </v-row>

    <v-row v-else-if="errorMessage" dense>
      <v-col cols="12">
        <v-alert class="state-card" type="error" variant="tonal">
          {{ errorMessage }}
        </v-alert>
      </v-col>
    </v-row>

    <template v-else>
      <div class="list-section">
        <div v-if="filteredSubscriptions.length" class="px-2">
          <h2 class="list-title">
            {{ listSectionTitle }}
          </h2>
        </div>

        <v-row v-if="!filteredSubscriptions.length" dense>
          <v-col cols="12">
            <div class="state-card empty-wrap">
              <div class="empty-icon-wrap">
                <v-icon size="28" color="primary">mdi-calendar-check-outline</v-icon>
              </div>
              <div class="empty-title">
                No {{ activeTab.toLowerCase() }} subscriptions
              </div>
              <div class="empty-subtitle">
                Try another tab or check back later for updated plans.
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row v-else dense>
          <v-col
            v-for="subscription in filteredSubscriptions"
            :key="subscription.id"
            cols="12"
          >
            <SubscriptionListCard
              :item="subscription"
              @click="handleOpen(subscription.id)"
            />
          </v-col>
        </v-row>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { fetchCustomerSubscriptions } from "@/shared/services/customers.api";
import SubscriptionListCard from "@/modules/customer/components/SubscriptionListCard.vue";

type SubscriptionStatus = "Active" | "Paused" | "Inactive" | "All";

interface SubscriptionItem {
  id: string | number;
  packageName: string;
  planAmount: number | null;
  billingInterval: string;
  nextBillingDate: string | null;
  status: SubscriptionStatus;
  vendorName: string;
  vendorLogo: string;
}

const router = useRouter();

const activeTab = ref<SubscriptionStatus>("Active");
const isLoading = ref(false);
const errorMessage = ref("");
const subscriptions = ref<SubscriptionItem[]>([]);

const filteredSubscriptions = computed(() => {
  return activeTab.value === "All"
    ? subscriptions.value
    : subscriptions.value.filter((item) => item.status === activeTab.value);
});

const subscriptionCounts = computed(() => {
  return subscriptions.value.reduce(
    (acc, item) => {
      acc.total += 1;
      if (item.status === "Active") acc.active += 1;
      if (item.status === "Paused") acc.paused += 1;
      if (item.status === "Inactive") acc.inactive += 1;
      return acc;
    },
    { total: 0, active: 0, paused: 0, inactive: 0 }
  );
});

const listSectionTitle = computed(() => {
  const label = activeTab.value.toLowerCase();
  return `${label.charAt(0).toUpperCase() + label.slice(1)} subscriptions`;
});

const heroMetricItems = computed(() => [
  {
    key: "Active" as const,
    label: "Active",
    value: subscriptionCounts.value.active,
    icon: "mdi-check-circle-outline",
    iconColor: "success",
  },
  {
    key: "Paused" as const,
    label: "Paused",
    value: subscriptionCounts.value.paused,
    icon: "mdi-pause-circle-outline",
    iconColor: "warning",
  },
  {
    key: "Inactive" as const,
    label: "Inactive",
    value: subscriptionCounts.value.inactive,
    icon: "mdi-close-circle-outline",
    iconColor: "grey",
  },
  {
    key: "All" as const,
    label: "All",
    value: subscriptionCounts.value.total,
    icon: "mdi-view-grid-outline",
    iconColor: "primary",
  },
]);

function normalizeStatus(status: unknown): SubscriptionStatus {
  const value = String(status ?? "")
    .trim()
    .toLowerCase();
  if (value === "paused" || value === "pending") return "Paused";
  if (value === "inactive" || value === "cancelled" || value === "canceled")
    return "Inactive";
  return "Active";
}

function handleOpen(id: string | number) {
  router
    .push({ name: "customer.subscriptions.detail", params: { id } })
    .catch(() => undefined);
}

async function loadSubscriptions() {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    const response: any = await fetchCustomerSubscriptions();
    const list = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
      ? response
      : [];

    subscriptions.value = list.map((item: any) => ({
      id: item.id,
      packageName: item.package_name ?? "",
      planAmount: item.plan_amount ?? null,
      billingInterval: item.billing_interval ?? item.plan?.type_name ?? "",
      nextBillingDate: item.next_billing_date ?? null,
      status: normalizeStatus(item.status),
      vendorName: item.vendor?.name ?? "",
      vendorLogo: item.vendor?.logo ?? "",
    }));
  } catch (err) {
    console.error("Failed to load subscriptions", err);
    errorMessage.value = "Unable to load subscriptions.";
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  void loadSubscriptions();
});
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 16px 12px 24px;
}

.hero-panel {
  display: grid;
  gap: 16px;
  /* padding: 18px; */
  border-radius: 18px;
  /* background: radial-gradient(
      circle at top right,
      rgba(25, 118, 210, 0.16),
      transparent 36%
    ),
    linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%); */
}

.hero-copy {
  display: grid;
  gap: 8px;
}

.eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(25, 118, 210, 0.9);
}

.hero-title {
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 800;
  color: #111827;
}

.hero-subtitle {
  margin: 0;
  max-width: 62ch;
  font-size: 14px;
  line-height: 1.65;
  color: #5b6472;
}

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.metric-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border-radius: var(--radius-lg);
  appearance: none;
  -webkit-appearance: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.metric-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.metric-card--selected {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.12), rgba(25, 118, 210, 0.05));
  border-color: rgba(25, 118, 210, 0.35);
}

.metric-icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #f8fafc;
}

.metric-icon--active {
  background: rgba(46, 125, 50, 0.1);
}

.metric-icon--paused {
  background: rgba(245, 124, 0, 0.1);
}

.metric-icon--inactive {
  background: rgba(97, 97, 97, 0.1);
}

.metric-icon--all {
  background: rgba(25, 118, 210, 0.1);
}

.metric-label {
  font-size: 14px;
  font-weight: 600;
  color: #637083;
}

.metric-value {
  font-size: 24px;
  line-height: 1;
  color: #111827;
  margin-left: auto;
  text-align: right;
  align-self: center;
}

.subscription-skeleton,
.state-card {
  border-radius: 20px;
}

.list-section {
  display: flex;
  flex-direction: column;
}

.list-title {
  margin: 0;
  padding: 0 2px;
  font-size: 16px;
  font-weight: 800;
  color: #111827;
}

.state-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #6b7280;
  background-color: #ffffff;
  padding: 28px 16px;
}

.empty-icon-wrap {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(25, 118, 210, 0.08);
}

.empty-title {
  font-weight: 700;
  font-size: 16px;
  color: #111827;
}

.empty-subtitle {
  font-size: 13px;
  color: #7b8494;
}
</style>
